import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { format, addDays } from "date-fns";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
  ArrowLeft, ShieldCheck, Lock, CreditCard, CheckCircle, CalendarBlank, MapPin,
  Phone, EnvelopeSimple, User, Buildings, Truck, Info,
} from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fetchPricing, fetchAvailability, createCheckoutSession } from "@/lib/api";
import { LOGO_URL, COMPANY } from "@/lib/site-data";
import { normalizeMaterial, MATERIALS, SINGLE_MATERIAL_LOADS, formatSelectionLine } from "@/lib/materials";
import { BOOK } from "@/constants/bookIds";
import WasteTypeSelector from "@/components/site/WasteTypeSelector";
import HelpMePickWizard from "@/components/site/HelpMePickWizard";
import CleanLoadGuarantee from "@/components/site/CleanLoadGuarantee";
import NoSurprisesGuarantee from "@/components/site/NoSurprisesGuarantee";
import TextPhotoCta from "@/components/site/TextPhotoCta";
import { Sparkle, Warning } from "@phosphor-icons/react";

const TIME_LABELS = {
  morning:        "Morning · 7 AM – 10 AM",
  late_morning:   "Late Morning · 10 AM – 1 PM",
  afternoon:      "Afternoon · 1 PM – 4 PM",
  late_afternoon: "Late Afternoon · 4 PM – 6 PM",
};

const inputCls =
  "w-full bg-white border-2 border-slate-900 px-4 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-sky-500 focus:ring-0 transition-colors";

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-slate-700">
          {label} {required && <span className="text-sky-500">*</span>}
        </span>
        {hint && <span className="text-[10px] text-slate-400">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function Section({ num, title, subtitle, icon: Icon, children }) {
  return (
    <section className="border-2 border-slate-900 bg-white">
      <header className="flex items-center gap-4 border-b-2 border-slate-900 px-6 py-5 bg-slate-50">
        <div className="flex items-center justify-center h-10 w-10 border-2 border-slate-900 bg-white">
          {Icon ? <Icon size={20} weight="bold" className="text-sky-600" /> : (
            <span className="font-display font-black text-slate-900">{num}</span>
          )}
        </div>
        <div>
          <div className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-sky-600">
            Step {num}
          </div>
          <div className="font-display font-bold text-lg text-slate-900 leading-tight">
            {title}
          </div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
      </header>
      <div className="p-6 sm:p-8">{children}</div>
    </section>
  );
}

function money(v) {
  return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(v);
}

function DayContentWithBadge(_availabilityMap) {
  // Capacity caps removed — no "N left" badge, just render the day number.
  return function DayContent(props) {
    return (
      <div className="flex flex-col items-center">
        <span>{props.date.getDate()}</span>
      </div>
    );
  };
}

export default function BookPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [pricingData, setPricingData] = useState(null);
  const [availability, setAvailability] = useState({ days: [] });

  // Selection
  const [category, setCategory] = useState(() => {
    const raw = params.get("category") || "Mixed Waste";
    return normalizeMaterial(raw) || raw;
  });
  const [size, setSize] = useState(params.get("size") || "");
  const [tierIndex, setTierIndex] = useState(parseInt(params.get("tier_index") || "0", 10));

  // Schedule
  const [dropDate, setDropDate] = useState(() => {
    const d = params.get("dropoff");
    if (d) {
      const parsed = new Date(d + "T12:00:00");
      if (!isNaN(parsed.getTime())) return parsed;
    }
    return undefined;
  });
  const [pickupDate, setPickupDate] = useState(() => {
    const d = params.get("dropoff");
    if (d) {
      const parsed = new Date(d + "T12:00:00");
      if (!isNaN(parsed.getTime())) return addDays(parsed, 7);
    }
    return undefined;
  });
  const [timeWindow, setTimeWindow] = useState("late_morning");
  const [sameDayAcknowledged, setSameDayAcknowledged] = useState(false);
  const [instructions, setInstructions] = useState("");

  // Contact
  const [cName, setCName] = useState("");
  const [cCompany, setCCompany] = useState("");
  const [cEmail, setCEmail] = useState("");
  const [cPhone, setCPhone] = useState("");

  // Billing
  const [bLine1, setBLine1] = useState("");
  const [bLine2, setBLine2] = useState("");
  const [bCity, setBCity] = useState("");
  const [bProvince, setBProvince] = useState("ON");
  const [bPostal, setBPostal] = useState("");

  // Shipping
  const [shipSame, setShipSame] = useState(true);
  const [sLine1, setSLine1] = useState("");
  const [sLine2, setSLine2] = useState("");
  const [sCity, setSCity] = useState("");
  const [sProvince, setSProvince] = useState("ON");
  const [sPostal, setSPostal] = useState("");

  // Terms & Conditions acceptance
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([fetchPricing(), fetchAvailability(21)])
      .then(([p, a]) => {
        setPricingData(p);
        setAvailability(a);
        if (!size && p?.pricing?.[category]?.length) {
          setSize(p.pricing[category][0].size);
        }
      })
      .catch(() => toast.error("Couldn't load booking info — try again"));
  }, []);

  // If the user arrived from the sizing-guide (Home wizard) with pre-filled
  // URL params, drop them directly on their pre-selected bin size card rather
  // than making them scroll from the top.
  useEffect(() => {
    if (!pricingData) return;
    const urlSize = params.get("size");
    const urlCat = params.get("category");
    if (!urlSize || !urlCat) return;
    const sizeSlug = urlSize.replace(/[^a-zA-Z0-9]/g, "");
    const cardId = `book-size-${sizeSlug}-${params.get("tier_index") || 0}`;
    setTimeout(() => {
      const target =
        document.querySelector(`[data-testid='${cardId}']`) ||
        document.querySelector("[data-testid='book-step-size']");
      if (target?.scrollIntoView) target.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingData]);

  const currentBin = useMemo(() => {
    if (!pricingData) return null;
    return (pricingData.pricing[category] || []).find((b) => b.size === size) || null;
  }, [pricingData, category, size]);

  const currentTier = currentBin?.tiers?.[tierIndex];
  const subtotal = currentTier?.price || 0;
  const tax = +(subtotal * (pricingData?.tax_rate || 0.13)).toFixed(2);
  const total = +(subtotal + tax).toFixed(2);

  const availabilityMap = useMemo(() => {
    const m = {};
    for (const d of availability.days || []) m[d.date] = d;
    return m;
  }, [availability]);

  // Windows for the selected drop-off date (server-computed: capacity, blocked, startable)
  const selectedDayInfo = useMemo(() => {
    if (!dropDate) return null;
    const iso = format(dropDate, "yyyy-MM-dd");
    return availabilityMap[iso] || null;
  }, [availabilityMap, dropDate]);

  const availableWindows = selectedDayInfo?.windows || [];
  const showSameDayNotice = !!selectedDayInfo?.same_day_after_cutoff;

  // Auto-fix window selection when day changes: if currently-selected window is
  // unavailable on the new date, pick the first one that IS available.
  useEffect(() => {
    if (!selectedDayInfo) return;
    const currentOk = availableWindows.find((w) => w.key === timeWindow && w.available);
    if (currentOk) return;
    const firstOk = availableWindows.find((w) => w.available);
    if (firstOk) setTimeWindow(firstOk.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDayInfo]);

  const dayContentComp = useMemo(() => DayContentWithBadge(availabilityMap), [availabilityMap]);

  function scrollToTestId(testid, block = "center") {
    setTimeout(() => {
      const el = document.querySelector(`[data-testid='${testid}']`);
      if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block });
    }, 60);
  }

  function handleCategoryChange(next) {
    setCategory(next);
    if (pricingData) {
      const bins = pricingData.pricing[next] || [];
      if (bins.length) {
        setSize(bins[0].size);
        setTierIndex(0);
      }
    }
    // Auto-scroll to Step 2 (bin size + tonnage)
    scrollToTestId("book-step-size", "start");
  }

  function handleSizeChange(nextSize) {
    setSize(nextSize);
    setTierIndex(0);
    // Auto-scroll to Step 3 (dates / book now)
    scrollToTestId("book-step-schedule", "start");
  }

  function handleWizardApply({ category: cat, size: sz, tier_index, dropoff_date }) {
    let effectiveSize = null;
    if (cat && pricingData?.pricing?.[cat]) {
      setCategory(cat);
      const bins = pricingData.pricing[cat] || [];
      effectiveSize = sz && bins.find((b) => b.size === sz) ? sz : bins[0]?.size;
      if (effectiveSize) setSize(effectiveSize);
      setTierIndex(tier_index || 0);
    }
    if (dropoff_date) {
      const d = new Date(dropoff_date + "T12:00:00");
      if (!isNaN(d.getTime())) {
        setDropDate(d);
        setPickupDate(addDays(d, 7));
      }
    }
    // Land the user *directly on their pre-selected bin size card* — not the
    // top of Book Now. Scroll to that specific card, not the section.
    setTimeout(() => {
      const sizeSlug = effectiveSize ? effectiveSize.replace(/[^a-zA-Z0-9]/g, "") : null;
      const cardTestId = sizeSlug ? `book-size-${sizeSlug}-${tier_index || 0}` : null;
      const target =
        (cardTestId && document.querySelector(`[data-testid='${cardTestId}']`)) ||
        document.querySelector("[data-testid='book-step-size']");
      if (target?.scrollIntoView) target.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
  }

  function handleDropChange(d) {
    setDropDate(d);
    if (d) {
      setPickupDate((prev) => (prev && prev >= d ? prev : addDays(d, 7)));
    }
  }

  function isDateDisabled(d) {
    const iso = format(d, "yyyy-MM-dd");
    const info = availabilityMap[iso];
    if (!info) {
      // Outside fetched window → disable
      return true;
    }
    return info.blocked;
  }

  async function onPay() {
    if (submitting) return;
    // Validate
    if (!currentBin || !currentTier) return toast.error("Pick a bin size");
    if (!dropDate) return toast.error("Pick a drop-off date");
    if (!pickupDate) return toast.error("Pick a pickup date");
    if (!timeWindow) return toast.error("Pick a delivery window");
    if (showSameDayNotice && !sameDayAcknowledged) {
      return toast.error("Please acknowledge the same-day disclaimer to continue");
    }
    if (!cName.trim() || !cEmail.trim() || !cPhone.trim()) {
      return toast.error("Fill in your contact info");
    }
    if (!bLine1.trim() || !bCity.trim() || !bPostal.trim()) {
      return toast.error("Fill in your billing address");
    }
    if (!shipSame && (!sLine1.trim() || !sCity.trim() || !sPostal.trim())) {
      return toast.error("Fill in your shipping address");
    }
    if (!termsAccepted) {
      return toast.error("Please accept the rental terms to continue");
    }

    setSubmitting(true);
    try {
      const payload = {
        category,
        size,
        tier_index: tierIndex,
        dropoff_date: format(dropDate, "yyyy-MM-dd"),
        time_window: timeWindow,
        pickup_date: format(pickupDate, "yyyy-MM-dd"),
        delivery_instructions: instructions.trim() || null,
        same_day_after_cutoff_acknowledged: sameDayAcknowledged,
        contact: { name: cName.trim(), email: cEmail.trim(), phone: cPhone.trim(), company: cCompany.trim() || null },
        billing_address: { line1: bLine1.trim(), line2: bLine2.trim() || null, city: bCity.trim(), province: bProvince, postal_code: bPostal.trim() },
        shipping_same_as_billing: shipSame,
        shipping_address: shipSame ? null : { line1: sLine1.trim(), line2: sLine2.trim() || null, city: sCity.trim(), province: sProvince, postal_code: sPostal.trim() },
        origin_url: window.location.origin,
      };
      const res = await createCheckoutSession(payload);
      if (res?.url) {
        window.location.href = res.url;
      } else {
        toast.error("Couldn't start payment — try again");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Payment couldn't be started";
      toast.error(typeof msg === "string" ? msg : "Failed to start checkout");
    } finally {
      setSubmitting(false);
    }
  }

  if (!pricingData) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">
        Loading booking…
      </div>
    );
  }

  const bins = pricingData.pricing[category] || [];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Top bar */}
      <header className="bg-white border-b-2 border-slate-900 sticky top-0 z-30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Troya" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-display font-black text-base text-slate-900">TROYA <span className="text-sky-500">MINI BINS</span></div>
              <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-slate-500">Secure Online Booking</div>
            </div>
          </Link>
          <Link
            to="/"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 border-2 border-slate-900 text-slate-900 font-bold text-xs uppercase tracking-[0.18em] hover:bg-slate-900 hover:text-white transition-colors"
          >
            <ArrowLeft size={16} weight="bold" /> Back
          </Link>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-10 lg:py-14">
        <div className="mb-8">
          <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-sky-600">Book online</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-2 leading-tight">
            Pick your bin. Pick your day. Pay securely.
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Transparent pricing, real-time availability, 256-bit secure card payment via Stripe.
            Your full order is confirmed the moment payment goes through.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Main form */}
          <div className="lg:col-span-8 space-y-6">
            {/* 1. Selection */}
            <Section num="1" title="Choose your waste type" subtitle="Pick the option that matches your load — pricing adjusts instantly" icon={Truck}>
              {/* Wizard CTA — replaces guesswork with a 30-second guide */}
              <HelpMePickWizard
                onApply={handleWizardApply}
                trigger={
                  <button
                    type="button"
                    data-testid="open-help-me-pick"
                    className="group w-full mb-6 flex items-center justify-between gap-4 p-4 sm:p-5 bg-gradient-to-r from-sky-50 via-white to-sky-50 border-2 border-sky-500 hover:bg-sky-500 hover:text-white transition-all duration-200 text-left"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="h-11 w-11 bg-sky-500 group-hover:bg-white group-hover:text-sky-500 text-white flex items-center justify-center shrink-0 transition-colors">
                        <Sparkle size={20} weight="fill" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-sky-600 group-hover:text-white/90 transition-colors">
                          New · 30-second guide
                        </div>
                        <div className="font-display font-bold text-base sm:text-lg text-slate-900 group-hover:text-white leading-tight mt-0.5 transition-colors">
                          Not sure which bin you need? Help me pick.
                        </div>
                        <div className="text-xs text-slate-600 group-hover:text-white/90 mt-1 transition-colors">
                          Three quick questions — we&apos;ll recommend the right size, material & drop-off date.
                        </div>
                      </div>
                    </div>
                    <ArrowLeft size={20} weight="bold" className="rotate-180 shrink-0 text-sky-500 group-hover:text-white transition-colors" />
                  </button>
                }
              />

              <WasteTypeSelector value={category} onChange={handleCategoryChange} />
            </Section>

            {/* 2. Bin size + tonnage */}
            <div data-testid="book-step-size">
            <Section num="2" title="Pick your bin size" subtitle="Price, material and included tonnage — one glance" icon={Truck}>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid={BOOK.selSize + "-cards"}>
                {bins.flatMap((b) =>
                  b.tiers.map((t, i) => {
                    const active = size === b.size && tierIndex === i;
                    return (
                      <button
                        type="button"
                        key={`${b.size}-${i}`}
                        onClick={() => { handleSizeChange(b.size); setTierIndex(i); }}
                        data-testid={`book-size-${b.size.replace(/[^a-zA-Z0-9]/g, "")}-${i}`}
                        className={`text-left border-2 p-4 transition-all ${
                          active
                            ? "border-sky-500 bg-sky-50 ring-4 ring-sky-200 -translate-y-0.5"
                            : "border-slate-900 hover:bg-slate-50 hover:-translate-y-0.5"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="font-display font-black text-xl text-slate-900">{b.size}</div>
                          <span className="text-slate-400 font-bold">—</span>
                          <div className="text-sm font-bold text-slate-700">{category}</div>
                        </div>
                        <div className="mt-1 text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">
                          {t.tons ? `Max ${t.tons} ton${t.tons > 1 ? "s" : ""}` : "Flat rate"}
                        </div>
                        <div className="font-display font-black text-2xl text-slate-900 mt-2">
                          {money(t.price)}
                          <span className="text-xs font-bold text-slate-500 ml-1">CAD</span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {size === "20 yd³" && (
                <div
                  data-testid="warning-20yd-heavy"
                  className="mt-5 border-2 border-amber-500 bg-amber-50 p-4 flex items-start gap-3"
                >
                  <Warning size={22} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-slate-900 leading-relaxed">
                    <span className="font-bold">Heavy materials in a 20 yd bin:</span>{" "}
                    If your load includes soil, concrete, brick, or asphalt mixed in with your
                    regular debris, keep it to about <span className="font-bold">4–5 wheelbarrow loads
                    total</span>. This keeps your bin safely haulable and helps you avoid overage charges.
                  </div>
                </div>
              )}

              {SINGLE_MATERIAL_LOADS.has(category) && (
                <div className="mt-5">
                  <CleanLoadGuarantee testid="clean-load-guarantee-book" />
                </div>
              )}

              <div className="mt-5 text-xs text-slate-500 leading-relaxed">
                Includes 7-day rental. Additional days $20/day. Overweight $0.15/kg, weighed on certified scale.
              </div>
            </Section>
            </div>

            {/* 3. Schedule */}
            <div data-testid="book-step-schedule">
            <Section num="3" title="Pick your dates &amp; time" subtitle="Toronto delivery · live schedule" icon={CalendarBlank}>
              {/* Blocked-date banner (capacity caps removed — no "fully booked" copy) */}
              {selectedDayInfo && selectedDayInfo.blocked && (
                <div
                  data-testid="book-day-capacity"
                  className="mb-4 border-2 border-rose-500 bg-rose-50 text-rose-900 px-4 py-3 text-sm flex items-center gap-3"
                >
                  <CalendarBlank size={18} weight="bold" className="shrink-0" />
                  <div className="font-semibold">
                    This date is unavailable. Please pick another day.
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Drop-off date" required hint="real-time availability">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid={BOOK.dropoffDate} className={`${inputCls} text-left flex items-center justify-between`}>
                        <span className={dropDate ? "text-slate-900" : "text-slate-400"}>
                          {dropDate ? format(dropDate, "EEE, MMM d, yyyy") : "Pick a date"}
                        </span>
                        <CalendarBlank size={20} weight="bold" className="text-slate-500" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-none border-2 border-slate-900" align="start">
                      <Calendar
                        mode="single"
                        selected={dropDate}
                        onSelect={handleDropChange}
                        disabled={isDateDisabled}
                        components={{ DayContent: dayContentComp }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Delivery Time Window" required hint="Delivery happens anytime within your selected window — driver may arrive at any point during it.">
                    <div className="grid sm:grid-cols-2 gap-3" data-testid={BOOK.timeWindow}>
                      {availableWindows.length === 0 && (
                        <div className="col-span-2 border-2 border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                          Pick a drop-off date to see available delivery windows.
                        </div>
                      )}
                      {availableWindows.map((w) => {
                        const isActive = timeWindow === w.key;
                        const disabled = !w.available;
                        const label = TIME_LABELS[w.key] || w.label;
                        const detail = w.blocked
                          ? "Unavailable"
                          : !w.startable
                            ? "Needs 2h prep — pick a later window"
                            : "Available";
                        return (
                          <button
                            key={w.key}
                            type="button"
                            onClick={() => !disabled && setTimeWindow(w.key)}
                            disabled={disabled}
                            data-testid={`book-window-${w.key}`}
                            aria-pressed={isActive}
                            className={`text-left border-2 px-4 py-3 transition-all ${
                              disabled
                                ? "border-slate-300 bg-slate-100 opacity-60 cursor-not-allowed"
                                : isActive
                                  ? "border-sky-500 bg-sky-50 ring-2 ring-sky-200"
                                  : "border-slate-900 hover:bg-slate-50"
                            }`}
                          >
                            <div className="font-display font-bold text-sm text-slate-900">{label}</div>
                            <div className={`text-[10px] font-bold tracking-[0.16em] uppercase mt-1 ${
                              disabled ? "text-slate-500" : "text-emerald-600"
                            }`}>
                              {detail}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {timeWindow && availableWindows.find((w) => w.key === timeWindow && w.available) && (
                      <div className="mt-3 text-xs text-slate-600 leading-relaxed">
                        <span className="font-bold text-slate-900">Heads up:</span> Delivery will occur
                        anytime within your selected delivery window. Our driver may arrive at any
                        time during this timeframe — please plan accordingly.
                      </div>
                    )}
                    {showSameDayNotice && (
                      <label
                        data-testid="same-day-ack-block"
                        className="mt-4 flex items-start gap-3 border-2 border-amber-500 bg-amber-50 p-4 cursor-pointer"
                      >
                        <Checkbox
                          data-testid="same-day-ack-checkbox"
                          checked={sameDayAcknowledged}
                          onCheckedChange={(v) => setSameDayAcknowledged(!!v)}
                          className="border-2 border-amber-600 rounded-none data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500 mt-0.5"
                        />
                        <span className="text-xs text-slate-900 leading-relaxed">
                          <span className="font-bold">Same-day service requested after our 12:00 PM cutoff.</span>{" "}
                          We will do our best to accommodate your selected delivery window, but same-day
                          delivery is not guaranteed until confirmed by our team. Check to acknowledge.
                        </span>
                      </label>
                    )}
                  </Field>
                </div>
                <Field label="Pickup date" required hint="default = drop + 7 days">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid={BOOK.pickupDate} className={`${inputCls} text-left flex items-center justify-between`}>
                        <span className={pickupDate ? "text-slate-900" : "text-slate-400"}>
                          {pickupDate ? format(pickupDate, "EEE, MMM d, yyyy") : "Pick a date"}
                        </span>
                        <CalendarBlank size={20} weight="bold" className="text-slate-500" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-none border-2 border-slate-900" align="start">
                      <Calendar
                        mode="single"
                        selected={pickupDate}
                        onSelect={setPickupDate}
                        disabled={(d) => (dropDate ? d < dropDate : d < new Date())}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </Field>
              </div>
              <div className="mt-5">
                <Field label="Delivery Instructions" hint="(optional)">
                  <textarea
                    data-testid={BOOK.instructions}
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    rows={3}
                    placeholder="Driveway placement, gate codes, low wires, anything else we should know…"
                    className={`${inputCls} resize-none`}
                  />
                </Field>
              </div>
            </Section>
            </div>

            {/* 4. Contact */}
            <Section num="4" title="Your contact details" icon={User}>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name" required>
                  <input data-testid={BOOK.cName} value={cName} onChange={(e) => setCName(e.target.value)} className={inputCls} placeholder="John Smith" />
                </Field>
                <Field label="Company" hint="(optional)">
                  <input data-testid={BOOK.cCompany} value={cCompany} onChange={(e) => setCCompany(e.target.value)} className={inputCls} placeholder="ACME Construction" />
                </Field>
                <Field label="Email" required>
                  <input data-testid={BOOK.cEmail} type="email" value={cEmail} onChange={(e) => setCEmail(e.target.value)} className={inputCls} placeholder="you@example.com" />
                </Field>
                <Field label="Phone" required>
                  <input data-testid={BOOK.cPhone} type="tel" value={cPhone} onChange={(e) => setCPhone(e.target.value)} className={inputCls} placeholder="(416) 555-1234" />
                </Field>
              </div>
            </Section>

            {/* 5. Billing */}
            <Section num="5" title="Billing address" icon={CreditCard}>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Street Address" required>
                  <input data-testid={BOOK.bLine1} value={bLine1} onChange={(e) => setBLine1(e.target.value)} className={inputCls} placeholder="123 Main St" />
                </Field>
                <Field label="Suite / Unit" hint="(optional)">
                  <input data-testid={BOOK.bLine2} value={bLine2} onChange={(e) => setBLine2(e.target.value)} className={inputCls} placeholder="Unit 4" />
                </Field>
                <Field label="City" required>
                  <input data-testid={BOOK.bCity} value={bCity} onChange={(e) => setBCity(e.target.value)} className={inputCls} placeholder="Toronto" />
                </Field>
                <Field label="Province" required>
                  <input data-testid={BOOK.bProvince} value={bProvince} onChange={(e) => setBProvince(e.target.value)} className={inputCls} placeholder="ON" />
                </Field>
                <Field label="Postal Code" required>
                  <input data-testid={BOOK.bPostal} value={bPostal} onChange={(e) => setBPostal(e.target.value)} className={inputCls} placeholder="M5V 2T6" />
                </Field>
              </div>
            </Section>

            {/* 6. Shipping */}
            <Section num="6" title="Delivery (drop-off) address" subtitle="Where the bin should be delivered" icon={MapPin}>
              <label className="flex items-center gap-3 mb-5 cursor-pointer">
                <Checkbox
                  data-testid={BOOK.shipSame}
                  checked={shipSame}
                  onCheckedChange={(v) => setShipSame(!!v)}
                  className="border-2 border-slate-900 rounded-none data-[state=checked]:bg-sky-500 data-[state=checked]:border-sky-500"
                />
                <span className="text-sm font-semibold text-slate-900">
                  Deliver to my billing address
                </span>
              </label>
              {!shipSame && (
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Street Address" required>
                    <input data-testid={BOOK.sLine1} value={sLine1} onChange={(e) => setSLine1(e.target.value)} className={inputCls} placeholder="456 Project Site Rd" />
                  </Field>
                  <Field label="Suite / Unit" hint="(optional)">
                    <input data-testid={BOOK.sLine2} value={sLine2} onChange={(e) => setSLine2(e.target.value)} className={inputCls} />
                  </Field>
                  <Field label="City" required>
                    <input data-testid={BOOK.sCity} value={sCity} onChange={(e) => setSCity(e.target.value)} className={inputCls} placeholder="Toronto" />
                  </Field>
                  <Field label="Province" required>
                    <input data-testid={BOOK.sProvince} value={sProvince} onChange={(e) => setSProvince(e.target.value)} className={inputCls} placeholder="ON" />
                  </Field>
                  <Field label="Postal Code" required>
                    <input data-testid={BOOK.sPostal} value={sPostal} onChange={(e) => setSPostal(e.target.value)} className={inputCls} placeholder="M5V 2T6" />
                  </Field>
                </div>
              )}
            </Section>
          </div>

          {/* Sidebar — summary + pay */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 space-y-5">
            <div className="border-2 border-slate-900 bg-white">
              <div className="bg-slate-900 text-white px-6 py-4">
                <div className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-sky-400">Order summary</div>
                <div className="font-display font-bold text-lg mt-1">Pay securely &amp; we&apos;ll deliver</div>
              </div>
              <div className="p-6 space-y-4">
                <div className="border-b border-slate-200 pb-4">
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">Your selection</div>
                  <div className="flex items-start gap-3 mt-2" data-testid="order-summary-line">
                    {MATERIALS[category]?.photo && (
                      <div className="h-14 w-14 border-2 border-slate-900 overflow-hidden shrink-0">
                        <img src={MATERIALS[category].photo} alt="" className="h-full w-full object-cover" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-display font-bold text-slate-900 leading-tight">
                        {size && category ? `${size} — ${category}` : category}
                      </div>
                      <div className="text-sm text-slate-600 mt-0.5">
                        {currentTier?.tons ? `Max ${currentTier.tons} ton${currentTier.tons > 1 ? "s" : ""}` : "Flat rate"}
                        {currentTier?.price ? ` — ${money(currentTier.price)}` : ""}
                      </div>
                    </div>
                  </div>
                </div>
                {dropDate && (
                  <div className="border-b border-slate-200 pb-4">
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">Drop-off</div>
                    <div className="text-sm text-slate-900 mt-1">{format(dropDate, "EEE, MMM d, yyyy")} · {TIME_LABELS[timeWindow]}</div>
                    {pickupDate && (
                      <div className="text-xs text-slate-500 mt-1">Pickup: {format(pickupDate, "EEE, MMM d, yyyy")}</div>
                    )}
                  </div>
                )}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-bold text-slate-900">{money(subtotal)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">HST (13%)</span><span className="font-bold text-slate-900">{money(tax)}</span></div>
                </div>
                <div className="border-t-2 border-slate-900 pt-3 flex justify-between items-baseline">
                  <span className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-slate-700">Total · CAD</span>
                  <span className="font-display font-black text-2xl text-slate-900">{money(total)}</span>
                </div>

                <label className="flex items-start gap-3 pt-3 border-t border-slate-200 cursor-pointer">
                  <Checkbox
                    data-testid="book-terms-accept"
                    checked={termsAccepted}
                    onCheckedChange={(v) => setTermsAccepted(!!v)}
                    className="border-2 border-slate-900 rounded-none data-[state=checked]:bg-sky-500 data-[state=checked]:border-sky-500 mt-0.5"
                  />
                  <span className="text-xs text-slate-700 leading-relaxed">
                    I agree to Troya Mini Bins&apos; rental terms — 7-day rental, $20/day for extra days,
                    $0.15/kg over the included tonnage, and no prohibited items.
                  </span>
                </label>

                <button
                  type="button"
                  onClick={onPay}
                  disabled={submitting}
                  data-testid={BOOK.payButton}
                  className="mt-4 w-full inline-flex items-center justify-center gap-3 px-6 py-4 bg-sky-500 hover:bg-sky-600 text-white font-bold text-sm uppercase tracking-[0.18em] border-2 border-sky-500 hover:border-sky-600 transition-colors disabled:opacity-60"
                >
                  {submitting ? "Redirecting to Stripe…" : (<><Lock size={16} weight="bold" /> Pay {money(total)} securely</>)}
                </button>

                <div className="flex items-center justify-center gap-4 pt-3 text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">
                  <span className="inline-flex items-center gap-1"><ShieldCheck size={14} weight="fill" className="text-emerald-600" /> SSL</span>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1"><Lock size={12} weight="fill" /> PCI</span>
                  <span>·</span>
                  <span>Powered by Stripe</span>
                </div>
              </div>
            </div>

            <NoSurprisesGuarantee testid="no-surprises-guarantee-book" />

            {/* Trust block */}
            <div className="border-2 border-emerald-600 bg-emerald-50 p-5">
              <div className="flex items-start gap-3">
                <ShieldCheck size={28} weight="fill" className="text-emerald-700 shrink-0" />
                <div>
                  <div className="font-display font-bold text-slate-900">Safe &amp; secure payments</div>
                  <p className="text-xs text-slate-700 mt-1 leading-relaxed">
                    Your card is processed on Stripe&apos;s PCI-DSS Level 1 platform. Troya Mini Bins never sees or stores
                    your card number. 256-bit TLS encryption end-to-end.
                  </p>
                </div>
              </div>
            </div>

            {/* Cancellation */}
            <div className="border-2 border-slate-900 bg-white p-5">
              <div className="flex items-start gap-3">
                <Info size={22} weight="bold" className="text-sky-600 shrink-0" />
                <div className="text-xs text-slate-700 leading-relaxed">
                  <div className="font-display font-bold text-sm text-slate-900 mb-1">Cancellation policy</div>
                  Free cancellation up to 24 hours before drop-off. Cancel inside 24 hours and a $50 fee applies
                  (we&apos;ve already routed the truck for your slot).
                </div>
              </div>
            </div>

            <div className="text-xs text-slate-500 text-center">
              Need help? Call <a href={`tel:${COMPANY.phoneRaw}`} className="font-bold text-sky-600">{COMPANY.phone}</a>
            </div>

            <TextPhotoCta testid="text-photo-cta-book" />
          </aside>
        </div>
      </div>

      <Toaster position="top-center" richColors />
    </div>
  );
}
